import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Reply, CheckCircle2, Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import client, { formatApiError } from "@/lib/api";

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => client.get("/admin/feedback").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openReply = (f) => {
    setActive(f);
    setSubject("Re: your MELLETIC feedback");
    setMessage("");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Write a reply first.");
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/feedback/${active.id}/reply`, { message: message.trim(), subject: subject.trim() });
      if (data.sent) toast.success(`Reply emailed to ${data.email}`);
      else toast.warning(`Saved, but email is disabled (would go to ${data.email})`);
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-extrabold mb-1">Feedback</h1>
      <p className="text-slate-500 mb-6">{items.length} messages from users.</p>
      {items.length === 0 ? <p className="text-slate-400">No feedback yet.</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-feedback-list">
          {items.map((f) => (
            <div key={f.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid={`feedback-card-${f.id}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <span className="font-semibold block truncate">{f.user_name || "User"}</span>
                  {f.user_email && <span className="text-xs text-slate-400 truncate block">{f.user_email}</span>}
                </div>
                <span className="flex gap-0.5 shrink-0">{[1,2,3,4,5].map((n) => <Star key={n} size={15} className={n <= f.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"} />)}</span>
              </div>
              {f.comment && <p className="text-slate-600 mt-2 flex-1">{f.comment}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">{(f.created_at || "").slice(0, 10)}</span>
                {f.replied_at ? (
                  <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid={`feedback-replied-${f.id}`}><CheckCircle2 size={14} /> Replied</span>
                ) : (
                  <button onClick={() => openReply(f)} disabled={!f.user_email} data-testid={`feedback-reply-btn-${f.id}`}
                    className="flex items-center gap-1.5 text-xs font-bold text-violet-600 hover:text-violet-800 disabled:text-slate-300 disabled:cursor-not-allowed">
                    <Reply size={14} /> {f.user_email ? "Reply by email" : "No email"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-[460px] rounded-3xl border-0 p-0 overflow-hidden bg-white" data-testid="feedback-reply-modal">
          <div className="px-6 pt-7 pb-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-11 h-11 rounded-2xl bg-violet-50 border border-violet-100 flex items-center justify-center text-violet-600"><Mail size={22} /></span>
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-extrabold text-slate-900">Reply to feedback</h2>
                <p className="text-sm text-slate-500 truncate">To {active?.user_name || "User"} · {active?.user_email}</p>
              </div>
            </div>
            {active?.comment && (
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 mb-4 text-sm text-slate-600 italic">"{active.comment}"</div>
            )}
            <form onSubmit={send} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="feedback-reply-subject"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium outline-none focus:border-violet-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Message</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Write your reply…" data-testid="feedback-reply-message"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-500 resize-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="feedback-reply-send"
                className="w-full mel-gradient text-white font-bold py-3 rounded-2xl press disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <Reply size={17} />} Send reply
              </button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
